/**
 * Accessibility projection for the datepicker field widget — WAI-ARIA grid
 * pattern (`role="grid"`/`role="gridcell"`), matching how
 * `calendarKeyboardTarget`'s own doc comment already describes the
 * interaction model this implements.
 */
import { MDY_WIDGET_CONTRACTS } from "../catalog.js";
import { fieldDescribedBy } from "./shell-a11y.js";
import { projectOverlayOpenerA11y } from "../opener-a11y.js";
import { assertUsableWidgetId } from "../ids.js";
import { MDY_FIELD_SHELL_CLASSES } from "../structure.js";
import { errorsVisible, holdsUneditedValue, shownErrors } from "./verdict.js";
import { fieldShellRootClasses } from "./shell-a11y.js";
export function datepickerFieldPartIds(widgetId) {
    assertUsableWidgetId(widgetId);
    return {
        labelId: `${widgetId}__label`,
        triggerId: `${widgetId}__trigger`,
        gridId: `${widgetId}__grid`,
        headingId: `${widgetId}__heading`,
        descriptionId: `${widgetId}__description`,
        errorId: `${widgetId}__errors`,
    };
}
/** The root's classes, from the shared table every kind reads. */
export function datepickerFieldRootClasses(state) {
    return fieldShellRootClasses(state);
}
export function projectDatepickerFieldA11y(state, errors, options) {
    const { labelId, triggerId, gridId, headingId, descriptionId, errorId } = datepickerFieldPartIds(options.widgetId);
    // Out of play, no verdict — the wrapper, the label, `aria-invalid` and whether the error
    // text renders are four faces of one question, answered once in verdict.ts.
    const hasErrors = shownErrors(state, errors).length > 0;
    // Whether the person is being told yet — `aria-invalid` says the same thing the error list does.
    // A rule they have not answered waits for them to reach the field; a refusal about the value
    // already there does not, because they can neither cause it by inaction nor see the reason unless
    // it is said.
    const tellingThem = errorsVisible({ disabled: state.disabled, touched: state.touched, holdsUnedited: holdsUneditedValue(state, "datepicker") }, errors);
    // Both, error first — an error does not take the place of the instruction that would have
    // prevented it. The container is pointed at while it is on the page, which is not the same as
    // while it holds a message: a renderer that reserves it keeps one reference that never changes.
    const describedBy = fieldDescribedBy({
        errorId,
        descriptionId,
        errorsPresent: options.errorsReserved ?? hasErrors,
        // A description exists when the renderer says one does. Defaulted to `true`, a control claimed
        // `aria-describedby` at rest and pointed it at an element holding nothing — which asserts that a
        // description exists and sends a reader to a text nobody wrote, and makes "I have one, empty"
        // indistinguishable from "I have none". Silence is the honest statement of nothing to say.
        descriptionPresent: options.supportingText === true || (options.supportingText ?? "") !== "",
    });
    return {
        root: {
            classes: datepickerFieldRootClasses(state),
            attributes: {},
        },
        label: {
            id: labelId,
            classes: [MDY_FIELD_SHELL_CLASSES.label],
            attributes: { for: triggerId },
        },
        trigger: {
            id: triggerId,
            classes: [...MDY_WIDGET_CONTRACTS.datepicker.parts.control.classes],
            attributes: {
                role: "combobox",
                ...projectOverlayOpenerA11y("datepicker", { widgetId: options.widgetId, open: state.open })
                    ?.attributes,
                "aria-labelledby": labelId,
                "aria-invalid": String(tellingThem),
                "aria-required": String(state.required),
                // `aria-disabled` reflects `disabled` alone. A read-only control is not disabled: it takes
                // focus, its text can be selected and copied, and announcing it as disabled tells a
                // screen-reader user they cannot interact with something they can. `aria-readonly`
                // carries read-only, and only on the kinds that declare the state.
                "aria-disabled": String(state.disabled),
                // A read-only field refuses the change and stays in play: focusable, submitted, validated.
                // What refuses it is the controller, and this is what says so — the state belongs on the
                // part the contract names as its carrier, which is the one a person operates.
                "aria-readonly": state.readonly ? "true" : null,
                // The native half as well, on a control whose text a person types: the platform stops the
                // typing and the ARIA says why. On a control HTML ignores `readonly` for, the widget's own
                // refusal is what holds and this attribute is left off rather than written as a claim
                // nothing acts on.
                readonly: state.readonly,
                "aria-describedby": describedBy,
                // The native attribute too, not only the ARIA one. Where this part lands on the typeable
                // input, `aria-disabled` alone would announce a disabled field that still accepts a typed
                // date.
                disabled: state.disabled,
            },
        },
        grid: {
            id: gridId,
            classes: ["mdy-datepicker__grid"],
            attributes: {
                role: "grid",
                /**
                 * Named by the line that says which month it shows, referred to rather than repeated.
                 *
                 * Named after the field it told a reader what they were filling in and not where they were:
                 * moving to the next month changed everything under the cursor and the name stayed. And
                 * never a written label beside this — a reference wins the name computation always, so a
                 * caption a renderer computed reached nobody while looking correct. ADR 0212.
                 */
                "aria-labelledby": headingId,
            },
        },
        description: {
            id: descriptionId,
            classes: [MDY_FIELD_SHELL_CLASSES.supportingText],
            attributes: {},
            ...(typeof options.supportingText === "string" && options.supportingText !== ""
                ? { content: { text: options.supportingText } } : {}),
        },
        error: {
            id: errorId,
            classes: [MDY_FIELD_SHELL_CLASSES.errors],
            attributes: {
                // A live region and nothing more. `role="alert"` here would override the list semantics of
                // the <ul> it sits on: axe reports every <li> inside such a list as an orphaned list item, and
                // a screen reader sees the same thing. `aria-live` announces the list when it appears, so the
                // role would cost the structure and add nothing.
                "aria-live": "polite",
            },
        },
    };
}
